import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { z } from "zod";

const playbookConfigurationAssistantPrefillSchema = z.object({
  message: z.string().min(1),
});

export type PlaybookConfigurationAssistantPrefill = z.infer<
  typeof playbookConfigurationAssistantPrefillSchema
>;

/**
 * Parse the prefill query param from the URL once on mount and clear it.
 * Mirrors the configuration-assistant prefill hook but with a schema scoped
 * to the playbook editor's chat (raw `message` sent verbatim).
 */
export function usePlaybookPrefillFromUrl(): PlaybookConfigurationAssistantPrefill | null {
  const [searchParams, setSearchParams] = useSearchParams();

  const [prefill] = useState<PlaybookConfigurationAssistantPrefill | null>(
    () => {
      const prefillParam = searchParams.get("prefill");
      if (!prefillParam) {
        return null;
      }

      try {
        return playbookConfigurationAssistantPrefillSchema.parse(
          JSON.parse(prefillParam),
        );
      } catch {
        return null;
      }
    },
  );

  const hasClearedPrefillRef = useRef(false);
  useEffect(() => {
    if (!hasClearedPrefillRef.current && searchParams.has("prefill")) {
      hasClearedPrefillRef.current = true;
      setSearchParams(
        (prev) => {
          prev.delete("prefill");
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams]);

  return prefill;
}
